import { Module } from 'vuex';

import { formsService } from '@/services/forms/forms.service';
import { orderLinesService } from '@/services/order-lines/order-lines.service';
import { OrderLine, OrderLineDuplicationData } from '@/services/order-lines/order-lines.types';
import { ordersService } from '@/services/orders/orders.service';
import { RootState } from '@/store';

// crypto.randomUUID() is not available in the TS lib types shipped with the current TypeScript version
function generateUuid(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export interface OrderLineState {
  orderLine: OrderLine | null;
  orderLineLoading: boolean;
  carrierLabelLoading: boolean;
  preparationLabelLoading: boolean;
  invoiceLoading: boolean;
  ordersImporting: boolean;
  isProposed?: boolean;
  isBundleOrder: boolean;
  bundleAccessories: OrderLine[];
  /* is_bundle of the order a line is being created for ; captured before the order focus is cleared */
  creationOrderIsBundle: boolean;
}

const state: OrderLineState = {
  orderLine: null,
  orderLineLoading: false,
  carrierLabelLoading: false,
  preparationLabelLoading: false,
  invoiceLoading: false,
  ordersImporting: false,
  isProposed: false,
  isBundleOrder: false,
  bundleAccessories: [],
  creationOrderIsBundle: false
};

const options: Module<OrderLineState, RootState> = {
  namespaced: true,
  state: () => state,
  actions: {
    setOrderLine: ({ commit, dispatch }, orderLine: OrderLine | string | number): Promise<void> => {
      if (typeof orderLine === 'number' || typeof orderLine === 'string') return dispatch('getOrderLine', orderLine);
      else {
        commit('orderLine', orderLine);
        return Promise.resolve();
      }
    },
    newOrderLine: (
      { commit, dispatch },
      { orderId, orderIsBundle }: { orderId: number; orderIsBundle: boolean }
    ): Promise<void> => {
      commit('orderLine', null);
      commit('creationOrderIsBundle', orderIsBundle);
      commit('orderLineLoading', true);
      return orderLinesService
        .newOrderLine()
        .then(orderLine => {
          if (orderLine != null) {
            orderLine.order_id = orderId;
            orderLine && commit('orderLine', orderLine);
          }
        })
        .catch(error => dispatch('alert/pushError', error, { root: true }))
        .finally(() => commit('orderLineLoading', false));
    },
    createOrderLine: ({ commit, dispatch }, orderLine: OrderLine): Promise<void> => {
      commit('orderLineLoading', true);
      return orderLinesService
        .create(orderLine)
        .then(orderLine => orderLine && commit('orderLine', orderLine))
        .then(() => dispatch('alert/pushSuccess', 'Commande créée avec succès !', { root: true }))
        .catch(error => dispatch('alert/pushError', error, { root: true }))
        .finally(() => commit('orderLineLoading', false));
    },
    updateOrderLine: async ({ commit, dispatch }, orderLine: OrderLine): Promise<void> => {
      commit('orderLineLoading', true);
      try {
        await orderLinesService.update(orderLine.id, orderLine);
        if (orderLine.order !== undefined) {
          await ordersService.update(orderLine.order.id, orderLine);
        }
        dispatch('setOrderLine', orderLine.id);
        dispatch('alert/pushSuccess', 'Commande/ligne de commande mise à jour !', { root: true });
      } catch (error) {
        dispatch('alert/pushError', error, { root: true });
      } finally {
        commit('orderLineLoading', false);
      }
    },
    deleteOrderLine: ({ commit, dispatch }, orderLineId: number): Promise<void> => {
      commit('orderLineLoading', true);
      return orderLinesService
        .delete(orderLineId)
        .then(() => commit('order-line', null))
        .then(() => dispatch('alert/pushSuccess', 'Ligne de commande supprimée !', { root: true }))
        .catch(error => dispatch('alert/pushError', error, { root: true }))
        .finally(() => commit('orderLineLoading', false));
    },
    customerProposal: ({ commit, dispatch }, orderLine: OrderLine): Promise<void> => {
      commit('orderLineLoading', true);

      return orderLinesService
        .customerProposal(orderLine.id)
        .then(orderLine => orderLine && commit('orderLine', orderLine))
        .then(() => dispatch('alert/pushSuccess', 'Proposition client faite !', { root: true }))
        .catch(error => dispatch('alert/pushError', error, { root: true }))
        .finally(() => commit('orderLineLoading', false));
    },
    validateCustomerProposal: (
      { commit, dispatch },
      payload: { orderLineId: number; choice: 'accepted' | 'refused' }
    ): Promise<void> => {
      commit('orderLineLoading', true);
      const message = payload.choice === 'accepted' ? 'Proposition client acceptée !' : 'Proposition client refusée !';
      return orderLinesService
        .validateCustomerProposal(payload.orderLineId, payload.choice)
        .then(orderLine => orderLine && commit('orderLine', orderLine))
        .then(() => dispatch('alert/pushSuccess', message, { root: true }))
        .catch(error => dispatch('alert/pushError', error, { root: true }))
        .finally(() => commit('orderLineLoading', false));
    },
    proposeAlternativeDevice: (
      { commit, dispatch },
      payload: { orderLineId: number; serialNo: string }
    ): Promise<void> => {
      commit('orderLineLoading', true);
      return orderLinesService
        .proposeAlternativeDevice(payload.orderLineId, payload.serialNo)
        .then(orderLine => orderLine && commit('orderLine', orderLine))
        .then(() => dispatch('alert/pushSuccess', 'Nouvelle proposition envoyée au client !', { root: true }))
        .catch(error => dispatch('alert/pushError', error, { root: true }))
        .finally(() => commit('orderLineLoading', false));
    },
    resetCarrierLabel: ({ commit, dispatch }, orderLineId: number): Promise<void> => {
      commit('orderLineLoading', true);
      return orderLinesService
        .resetCarrierLabel(orderLineId)
        .then(orderLine => orderLine && commit('orderLine', orderLine))
        .then(() => dispatch('alert/pushSuccess', 'Reset du BL effectué', { root: true }))
        .catch(error => dispatch('alert/pushError', error, { root: true }))
        .finally(() => commit('orderLineLoading', false));
    },
    getOrderLine: ({ commit, dispatch }, orderLineId: number): Promise<void> => {
      commit('orderLineLoading', true);
      return orderLinesService
        .get(orderLineId)
        .then(orderLine => orderLine && commit('orderLine', orderLine))
        .catch(error => dispatch('alert/pushError', error, { root: true }))
        .finally(() => commit('orderLineLoading', false));
    },
    duplicateOrderLine: ({ commit, dispatch }, options: OrderLineDuplicationData): Promise<void> => {
      commit('orderLineLoading', true);
      return orderLinesService
        .duplicate(options.order_line_id, options.n_times)
        .then(() => dispatch('alert/pushSuccess', `Produit dupliqué ${options.n_times} fois !`, { root: true }))
        .catch(error => dispatch('alert/pushError', error, { root: true }))
        .finally(() => commit('orderLineLoading', false));
    },
    createExchangeOrderLine: ({ commit, dispatch, getters }, orderLine: OrderLine): Promise<void> => {
      commit('orderLine', orderLine);
      return dispatch('createOrderLine', getters.orderLineExchangeCreationForm.value);
    },
    printCarrierLabel: ({ commit, dispatch }, orderLineId: number): Promise<void> => {
      commit('carrierLabelLoading', true);
      return orderLinesService
        .getCarrierLabel(orderLineId)
        .then(resp => {
          const promises = [
            dispatch('setOrderLine', orderLineId).catch(_err => _err), // Non-blocking UI reload
            dispatch('alert/pushInfo', 'Impression du bon de transport...', { root: true })
          ];
          if (resp?.zpl) {
            // Physical printing must not block the rest of the flow: the label/tracking number is already saved server-side.
            promises.push(
              dispatch('printer/printWith', { data: [resp.zpl], printer: 'SHIPPING' }, { root: true }).catch(error => {
                dispatch('alert/pushError', error, { root: true });
              })
            );
          }
          return Promise.all(promises).then(() => undefined);
        })
        .catch(error => {
          dispatch('alert/pushError', error, { root: true });
          throw error;
        })
        .finally(() => commit('carrierLabelLoading', false));
    },
    printPreparationLabel: ({ commit, dispatch }, orderId: number): Promise<void> => {
      commit('preparationLabelLoading', true);
      return orderLinesService
        .getPreparationLabel(orderId)
        .then(
          resp => resp?.zpl && dispatch('printer/printWith', { data: [resp.zpl], printer: 'PREPA' }, { root: true })
        )
        .catch(error => dispatch('alert/pushError', error, { root: true }))
        .finally(() => commit('preparationLabelLoading', false));
    },
    importOrders: ({ commit, dispatch }, { marketplace, file }: { marketplace: string; file: File }): Promise<void> => {
      commit('ordersImporting', true);
      return orderLinesService
        .importOrders(marketplace, file)
        .then(resp =>
          dispatch(
            'alert/pushSuccess',
            `${resp.count} commande${resp.count > 1 ? 's' : ''} importée${resp.count > 1 ? 's' : ''} !`,
            { root: true }
          )
        )
        .catch(error => dispatch('alert/pushError', error, { root: true }))
        .finally(() => commit('ordersImporting', false));
    },
    setIsBundleOrder: ({ commit }, value: boolean): void => {
      commit('isBundleOrder', value);
    },
    getBundleAccessories: ({ commit, dispatch }, bundleGroupId: string): Promise<void> => {
      return orderLinesService
        .getByBundleGroup(bundleGroupId)
        .then(lines => {
          const accessories = lines.filter(l => l.order_line_type === 'accessory');
          commit('bundleAccessories', accessories);
        })
        .catch(error => dispatch('alert/pushError', error, { root: true }));
    },
    clearBundleAccessories: ({ commit }): void => {
      commit('bundleAccessories', []);
    },
    createBundleOrderLines: async (
      { commit, dispatch },
      { baseOrderLine, items }: { baseOrderLine: OrderLine; items: Partial<OrderLine>[] }
    ): Promise<void> => {
      commit('orderLineLoading', true);
      try {
        // Shared identifier used to link all lines created together as a single bundle
        const bundleGroupId = generateUuid();
        for (const item of items) {
          await orderLinesService.create({ ...baseOrderLine, ...item, bundle_group_id: bundleGroupId } as OrderLine);
        }
        if (baseOrderLine.order_id) {
          await ordersService.update(baseOrderLine.order_id, { is_bundle: true });
        }
        dispatch(
          'alert/pushSuccess',
          `${items.length} ligne${items.length > 1 ? 's' : ''} de commande créée${items.length > 1 ? 's' : ''} !`,
          { root: true }
        );
      } catch (error) {
        dispatch('alert/pushError', error, { root: true });
        throw error;
      } finally {
        commit('orderLineLoading', false);
      }
    }
  },
  mutations: {
    orderLine: (state, orderLine) => (state.orderLine = orderLine),
    orderLineLoading: (state, loading) => (state.orderLineLoading = loading),
    ordersImporting: (state, importing) => (state.ordersImporting = importing),
    carrierLabelLoading: (state, loading) => (state.carrierLabelLoading = loading),
    preparationLabelLoading: (state, loading) => (state.preparationLabelLoading = loading),
    invoiceLoading: (state, loading) => (state.invoiceLoading = loading),
    isBundleOrder: (state, value) => (state.isBundleOrder = value),
    bundleAccessories: (state, accessories) => (state.bundleAccessories = accessories),
    creationOrderIsBundle: (state, value) => (state.creationOrderIsBundle = value)
  },
  getters: {
    orderLineCreationForm: (state, getters, rootState) =>
      formsService.getForm('order-line-creation', state.orderLine as OrderLine, rootState['global-settings'].settings),
    orderLineExchangeCreationForm: (state, getters, rootState) =>
      formsService.getForm(
        'order-line-exchange-creation',
        state.orderLine as OrderLine,
        rootState['global-settings'].settings
      ),
    orderLineForm: (state, getters, rootState, rootGetters) =>
      formsService.getForm('order-line', state.orderLine as OrderLine, rootState['global-settings'].settings, {
        actionUpdateOrderShippingDate: rootGetters['user/actionUpdateOrderShippingDate']
      })
  }
};

export default options;
